import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Divider,
  Alert,
  IconButton,
  Avatar,
  CircularProgress,
  Grid,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Close as CloseIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  CreditCard as CreditCardIcon,
  Phone as PhoneIcon,
  LinkedIn as LinkedInIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  AccessTime as AccessTimeIcon,
  Star as StarIcon,
  Subscriptions as SubscriptionsIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
  Group as GroupIcon,
  Download as DownloadIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import api from '../api';
import logger from '../utils/logger';
import AddOnsSection from './AddOnsSection';

const UserSettingsDialog = ({ open, onClose, initialTab = 0, scrollToAddOns = false }) => {
  const { user, team, refreshUser, refreshSubscriptionData, isAdmin, isSupervisor } = useAuth();
  const { t, isNL } = useLanguage();
  
  // Ref for add-ons section to scroll to
  const addOnsSectionRef = useRef(null);
  
  // Safe translation function with English fallbacks
  const safeT = (key) => {
    const englishFallbacks = {
      'userSettings': 'User Settings',
      'userInformation': 'User Information',
      'displayName': 'Display Name',
      'emailAddress': 'Email Address',
      'changePassword': 'Change Password',
      'currentPassword': 'Current Password',
      'newPassword': 'New Password',
      'confirmPassword': 'Confirm Password',
      'optional': 'Optional',
      'currentPasswordRequired': 'Current password is required',
      'newPasswordRequired': 'New password is required',
      'passwordsDoNotMatch': 'Passwords do not match',
      'passwordChanged': 'Password changed successfully',
      'profileUpdated': 'Profile updated successfully',
      'lushaCredits': 'Contact Credits',
      'totalCreditsUsed': 'Total Credits Used',
      'totalEnrichments': 'Total Enrichments',
      'recentCreditUsage': 'Recent Credit Usage',
      'date': 'Date',
      'contactName': 'Contact Name',
      'company': 'Company',
      'credits': 'Credits',
      'type': 'Type',
      'emailEnriched': 'Email enriched',
      'phoneEnriched': 'Phone enriched',
      'linkedinEnriched': 'LinkedIn enriched',
      'noCreditUsageFound': 'No credit usage found',
      'cancel': 'Cancel',
      'loading': 'Loading...',
      'saveChanges': 'Save Changes',
      'dataImportTemplate': 'Data Import Template',
      'downloadImportTemplateDescription': 'Download the import template file to prepare your data. Contact your Administrator or Supervisor to have the completed file imported.',
      'downloadImportTemplateDescriptionNL': 'Download het import template bestand om gegevens voor te bereiden. Neem contact op met uw Administrator of Supervisor om het ingevulde bestand te laten importeren.',
      'downloadImportTemplate': 'Download Import Template'
    };
    
    try {
      const translated = t(key);
      // If the translation returns the key itself (meaning no translation found), use fallback
      if (translated === key || !translated) {
        return englishFallbacks[key] || key;
      }
      return translated;
    } catch (error) {
      logger.warn(`Translation failed for key "${key}":`, error);
      return englishFallbacks[key] || key;
    }
  };

  // Form state
  const [formData, setFormData] = useState({
    displayName: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    showOutreachPerformance: false
  });
  
  const [showPasswordSection, setShowPasswordSection] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // Tab state - initialize with initialTab prop
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Update activeTab when initialTab prop changes (e.g., when opened from banner)
  useEffect(() => {
    if (open) {
      setActiveTab(initialTab);
      // Load subscription data if opening to subscription tab
      if (initialTab === 2 && user) {
        loadSubscriptionData();
      }
    }
  }, [open, initialTab, user]);
  
  // Lusha data state
  const [lushaData, setLushaData] = useState({
    creditUsage: [],
    enrichedContacts: [],
    summary: {
      total_credits: 0,
      total_enrichments: 0
    },
    personCreditUsage: [],
    personSummary: {
      total_credits: 0,
      total_searches: 0,
      successful_searches: 0,
      charged_searches: 0,
      success_rate: 0
    }
  });
  const [lushaLoading, setLushaLoading] = useState(false);

  // Subscription data state
  const [subscriptionData, setSubscriptionData] = useState({
    subscription: null,
    status: null,
    plans: []
  });
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [subscriptionActionLoading, setSubscriptionActionLoading] = useState(false);
  const [activeAddons, setActiveAddons] = useState([]);
  
  // Scroll to add-ons section when dialog opens with scrollToAddOns flag
  // This must be after subscriptionLoading is declared
  useEffect(() => {
    if (open && scrollToAddOns && activeTab === 2 && !subscriptionLoading) {
      // Wait for dialog content and subscription data to load, then scroll
      const scrollTimeout = setTimeout(() => {
        if (addOnsSectionRef.current) {
          // Find the DialogContent container to scroll within
          const dialogContent = addOnsSectionRef.current.closest('[class*="MuiDialogContent"]');
          if (dialogContent) {
            // Calculate the position relative to the dialog content
            const elementRect = addOnsSectionRef.current.getBoundingClientRect();
            const dialogRect = dialogContent.getBoundingClientRect();
            const scrollTop = dialogContent.scrollTop + (elementRect.top - dialogRect.top) - 20;
            
            dialogContent.scrollTo({
              top: scrollTop,
              behavior: 'smooth'
            });
          } else {
            // Fallback: scroll the element into view
            addOnsSectionRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
          }
        }
      }, 600); // Delay to ensure content is fully rendered
      
      return () => clearTimeout(scrollTimeout);
    }
  }, [open, scrollToAddOns, activeTab, subscriptionLoading, subscriptionData]);

  // Add confirmation modal state
  const [confirmationModal, setConfirmationModal] = useState({
    open: false,
    action: '',
    planId: null,
    planName: '',
    planPrice: '',
    isChangePlan: false
  });

  // Team members state (for supervisors)
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);

  // Initialize form with user data when dialog opens
  useEffect(() => {
    if (open && user) {
      setFormData({
        displayName: user.display_name || '',
        email: user.email || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        showOutreachPerformance: user.show_outreach_performance || false
      });
      setShowPasswordSection(false);
      setMessage({ type: '', text: '' });
      // Don't reset tab here - let the initialTab useEffect handle it
      // This prevents overriding the initialTab prop when opened from banner
    }
  }, [open, user]);

  const loadLushaData = async () => {
    if (!user) return;
    
    setLushaLoading(true);
    try {
      // Load contact enrichment credit usage for current user
      const creditResponse = await api.get(`/api/lusha/credit-usage?user_id=${user.id}`);
      
      // Load enriched contacts for current user  
      const contactsResponse = await api.get(`/api/lusha/enriched-contacts?user_id=${user.id}`);
      
      // Load person search credit usage for current user
      const personCreditResponse = await api.get(`/api/lusha/person-credit-usage?user_id=${user.id}`);
      
      setLushaData({
        creditUsage: creditResponse.data.success ? creditResponse.data.data : [],
        enrichedContacts: contactsResponse.data.success ? contactsResponse.data.data : [],
        summary: creditResponse.data.success ? creditResponse.data.summary : {},
        personCreditUsage: personCreditResponse.data.success ? personCreditResponse.data.data : [],
        personSummary: personCreditResponse.data.success ? personCreditResponse.data.summary : {
          total_credits: 0,
          total_searches: 0,
          successful_searches: 0,
          charged_searches: 0,
          success_rate: 0
        }
      });
    } catch (error) {
      logger.error('Error loading Lusha data:', error);
      setMessage({ type: 'error', text: 'Failed to load Lusha credit data' });
      
      // Set default values to prevent crashes
      setLushaData({
        creditUsage: [],
        enrichedContacts: [],
        summary: {
          total_credits: 0,
          total_enrichments: 0
        },
        personCreditUsage: [],
        personSummary: {
          total_credits: 0,
          total_searches: 0,
          successful_searches: 0,
          charged_searches: 0,
          success_rate: 0
        }
      });
    } finally {
      setLushaLoading(false);
    }
  };

  const loadSubscriptionData = async () => {
    if (!user) return;
    
    setSubscriptionLoading(true);
    try {
      // Load user's subscription status, available plans, and add-on status
      const [statusResponse, plansResponse, addonsStatusResponse] = await Promise.all([
        api.get('/api/subscription/status'),
        api.get('/api/subscription/plans'),
        api.getAddOnStatus().catch(() => ({ success: false, data: { addons: [] } }))
      ]);
      
      logger.log('Status response:', statusResponse.data);
      logger.log('Plans response:', plansResponse.data);
      
      setSubscriptionData({
        subscription: statusResponse.data.success ? statusResponse.data.data?.subscription : null,
        status: statusResponse.data.success ? statusResponse.data.data?.status : null,
        plans: plansResponse.data.success ? (Array.isArray(plansResponse.data.data?.plans) ? plansResponse.data.data.plans : []) : []
      });
      
      // Set active add-ons (those that are active or in trial)
      if (addonsStatusResponse.success && addonsStatusResponse.data?.addons) {
        setActiveAddons(addonsStatusResponse.data.addons.filter(addon => 
          addon.status === 'active' || addon.status === 'trial'
        ));
      } else {
        setActiveAddons([]);
      }
    } catch (error) {
      logger.error('Error loading subscription data:', error);
      setMessage({ type: 'error', text: 'Failed to load subscription data' });
      // Set safe defaults on error
      setSubscriptionData({
        subscription: null,
        status: null,
        plans: []
      });
      setActiveAddons([]);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const validateForm = () => {
    if (showPasswordSection) {
      if (!formData.currentPassword) {
        setMessage({ type: 'error', text: safeT('currentPasswordRequired') });
        return false;
      }
      if (!formData.newPassword) {
        setMessage({ type: 'error', text: safeT('newPasswordRequired') });
        return false;
      }
      if (formData.newPassword !== formData.confirmPassword) {
        setMessage({ type: 'error', text: safeT('passwordsDoNotMatch') });
        return false;
      }
      if (formData.newPassword.length < 6) {
        setMessage({ type: 'error', text: 'Password must be at least 6 characters long' });
        return false;
      }
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaveLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const updateData = {
        display_name: formData.displayName,
        email: formData.email,
        show_outreach_performance: formData.showOutreachPerformance
      };

      if (showPasswordSection) {
        updateData.current_password = formData.currentPassword;
        updateData.new_password = formData.newPassword;
      }

      const response = await api.updateUserProfile(updateData);
      
      if (response.success) {
        setMessage({ 
          type: 'success', 
          text: showPasswordSection ? safeT('passwordChanged') : safeT('profileUpdated')
        });
        
        // Refresh user data
        await refreshUser();
        
        // Reset password fields if they were used
        if (showPasswordSection) {
          setFormData(prev => ({
            ...prev,
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          }));
          setShowPasswordSection(false);
        }
        
        // Close dialog after a short delay
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setMessage({ type: 'error', text: response.message || 'An error occurred' });
      }
    } catch (error) {
      logger.error('Error updating profile:', error);
      const errorMessage = error.response?.data?.message || 'An error occurred while updating your profile';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setSaveLoading(false);
    }
  };

  const getUserInitials = (user) => {
    if (!user) return 'U';
    if (user.display_name) {
      const names = user.display_name.split(' ');
      if (names.length >= 2) {
        return (names[0][0] + names[names.length - 1][0]).toUpperCase();
      }
      return user.display_name.substring(0, 2).toUpperCase();
    }
    return user.username.substring(0, 2).toUpperCase();
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 1) {
      loadLushaData();
    } else if (newValue === 2) {
      loadSubscriptionData();
    } else if (newValue === 3 && user?.role_type === 'Supervisor') {
      loadTeamMembers();
    }
  };

  const handleSubscribe = async (planId) => {
    // Find the plan details
    const plan = subscriptionData.plans.find(p => p.id === planId);
    const isChangePlan = subscriptionData.status?.status === 'active';
    
    // Show confirmation modal
    setConfirmationModal({
      open: true,
      action: isChangePlan ? 'change' : 'subscribe',
      planId: planId,
      planName: plan?.name || '',
      planPrice: plan?.price_eur || '',
      isChangePlan: isChangePlan
    });
  };

  const handleConfirmSubscription = async () => {
    setSubscriptionActionLoading(true);
    setConfirmationModal({ ...confirmationModal, open: false });
    
    try {
      const response = await api.post('/api/subscription/activate', {
        plan_id: confirmationModal.planId
      });

      if (response.data.success) {
        setMessage({ 
          type: 'success', 
          text: confirmationModal.isChangePlan 
            ? 'Plan changed successfully!' 
            : 'Subscription activated successfully!' 
        });
        // Refresh subscription data locally
        await loadSubscriptionData();
        // Trigger global subscription refresh
        if (refreshSubscriptionData) {
          await refreshSubscriptionData();
        }
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Failed to activate subscription' });
      }
    } catch (error) {
      logger.error('Error subscribing:', error);
      setMessage({ type: 'error', text: 'An error occurred while activating subscription' });
    } finally {
      setSubscriptionActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    // Show confirmation modal for cancellation
    setConfirmationModal({
      open: true,
      action: 'cancel',
      planId: null,
      planName: subscriptionData.subscription?.plan_name || 'Current Plan',
      planPrice: '',
      isChangePlan: false
    });
  };

  const handleConfirmCancellation = async () => {
    setSubscriptionActionLoading(true);
    setConfirmationModal({ ...confirmationModal, open: false });
    
    try {
      const response = await api.post('/api/subscription/cancel', {
        reason: 'User requested cancellation'
      });

      if (response.data.success) {
        setMessage({ type: 'success', text: 'Subscription cancelled successfully!' });
        // Refresh subscription data locally
        await loadSubscriptionData();
        // Trigger global subscription refresh
        if (refreshSubscriptionData) {
          await refreshSubscriptionData();
        }
      } else {
        setMessage({ type: 'error', text: response.data.message || 'Failed to cancel subscription' });
      }
    } catch (error) {
      logger.error('Error cancelling subscription:', error);
      setMessage({ type: 'error', text: 'An error occurred while cancelling subscription' });
    } finally {
      setSubscriptionActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Load team members for supervisor
  const loadTeamMembers = async () => {
    setTeamLoading(true);
    try {
      const response = await api.getAssignedUsers();
      if (response.success && Array.isArray(response.users)) {
        setTeamMembers(response.users);
      } else {
        setTeamMembers([]);
      }
    } catch (error) {
      setTeamMembers([]);
    } finally {
      setTeamLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '400px' }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <SettingsIcon sx={{ mr: 1 }} />
          <Typography variant="h6">{safeT('userSettings')}</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {message.text && (
          <Alert 
            severity={message.type} 
            sx={{ mb: 2 }}
            onClose={() => setMessage({ type: '', text: '' })}
          >
            {message.text}
          </Alert>
        )}

        {/* User Avatar and Basic Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Avatar
            sx={{ 
              width: 64, 
              height: 64, 
              mr: 2,
              bgcolor: 'primary.main',
              fontSize: '1.5rem'
            }}
          >
            {getUserInitials(user)}
          </Avatar>
          <Box>
            <Typography variant="h6">
              {user?.display_name || user?.username}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              @{user?.username}
            </Typography>
            {user?.role_type && (
              <Typography variant="caption" color="text.secondary">
                {user.role_type}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Tabs */}
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
          <Tab 
            icon={<PersonIcon />} 
            label={safeT('userInformation')} 
            iconPosition="start"
          />
          <Tab 
            icon={<CreditCardIcon />} 
            label={safeT('lushaCredits')} 
            iconPosition="start"
          />
          <Tab 
            icon={<SubscriptionsIcon />} 
            label="Subscription" 
            iconPosition="start"
          />
          {user?.role_type === 'Supervisor' && (
            <Tab 
              icon={<GroupIcon />} 
              label="Team Members" 
              iconPosition="start"
            />
          )}
        </Tabs>

        {/* User Information Tab */}
        {activeTab === 0 && (
          <Box>
            {/* User Information Section */}
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1 }} />
              {safeT('userInformation')}
            </Typography>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={safeT('displayName')}
                  placeholder={!formData.displayName ? "Voer uw weergavenaam in" : undefined}
                  value={formData.displayName}
                  onChange={(e) => handleInputChange('displayName', e.target.value)}
                  variant="outlined"
                  helperText={`${safeT('optional')} - This name will be displayed in the application`}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={safeT('emailAddress')}
                  type="email"
                  placeholder={!formData.email ? "Voer uw e-mailadres in" : undefined}
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  variant="outlined"
                  helperText={`${safeT('optional')} - Used for notifications and account recovery`}
                />
              </Grid>
            </Grid>

            <Divider sx={{ mb: 2 }} />

            {/* User Preferences Section */}
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <SettingsIcon sx={{ mr: 1 }} />
              Preferences
            </Typography>

            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.showOutreachPerformance}
                    onChange={(e) => handleInputChange('showOutreachPerformance', e.target.checked)}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2">
                      Show Outreach Performance Section
                    </Typography>
                  </Box>
                }
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                Display your outreach statistics and performance metrics on the main page
              </Typography>
            </Box>

            {team && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                  <GroupIcon sx={{ mr: 1 }} />
                  Your team
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {team.name} ({team.company_domain}). You can see outreach from these people, and they can see yours.
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Username</TableCell>
                        <TableCell>Email</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(team.members || []).map((member) => (
                        <TableRow key={member.id || member.username}>
                          <TableCell>{member.display_name || member.username}</TableCell>
                          <TableCell>{member.username}</TableCell>
                          <TableCell>{member.email}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            <Divider sx={{ mb: 2 }} />

            {/* Password Section */}
            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showPasswordSection}
                    onChange={(e) => setShowPasswordSection(e.target.checked)}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <LockIcon sx={{ mr: 1 }} />
                    {safeT('changePassword')}
                  </Box>
                }
              />
            </Box>

            {showPasswordSection && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={safeT('currentPassword')}
                    type="password"
                    placeholder={!formData.currentPassword ? "Voer uw huidig wachtwoord in" : undefined}
                    value={formData.currentPassword}
                    onChange={(e) => handleInputChange('currentPassword', e.target.value)}
                    variant="outlined"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={safeT('newPassword')}
                    type="password"
                    placeholder={!formData.newPassword ? "Voer uw nieuw wachtwoord in" : undefined}
                    value={formData.newPassword}
                    onChange={(e) => handleInputChange('newPassword', e.target.value)}
                    variant="outlined"
                    required
                    helperText="Minimum 6 characters"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={safeT('confirmPassword')}
                    type="password"
                    placeholder={!formData.confirmPassword ? "Voer uw nieuw wachtwoord opnieuw in" : undefined}
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                    variant="outlined"
                    required
                  />
                </Grid>
              </Grid>
            )}

            {/* Template Download Section for Regular Users and Supervisors */}
            {!isAdmin && (
              <>
                <Divider sx={{ mb: 2, mt: 2 }} />
                <Box sx={{ mb: 2 }}>
                  <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                    <InfoIcon sx={{ mr: 1 }} />
                    {safeT('dataImportTemplate')}
                  </Typography>
                  <Paper sx={{ p: 2, backgroundColor: '#e3f2fd', mb: 2 }}>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                      {isNL ? safeT('downloadImportTemplateDescriptionNL') : safeT('downloadImportTemplateDescription')}
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<DownloadIcon />}
                      onClick={async () => {
                        try {
                          // Get backend URL from api instance
                          const backendUrl = api.defaults.baseURL || 'http://localhost:5000';
                          const downloadUrl = `${backendUrl}/api/files/download-template`;
                          
                          // Fetch the file with credentials
                          const response = await fetch(downloadUrl, {
                            method: 'GET',
                            credentials: 'include', // Include cookies for authentication
                          });
                          
                          if (!response.ok) {
                            throw new Error(`Failed to download template: ${response.status} ${response.statusText}`);
                          }
                          
                          // Get the file as a blob
                          const blob = await response.blob();
                          
                          // Create a temporary URL for the blob
                          const url = window.URL.createObjectURL(blob);
                          
                          // Create a link element and trigger download
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = 'Salinator_Company_Import_Template_filters.xlsx';
                          document.body.appendChild(link);
                          link.click();
                          
                          // Clean up
                          document.body.removeChild(link);
                          window.URL.revokeObjectURL(url);
                          
                          setMessage({ type: 'success', text: 'Template downloaded successfully' });
                        } catch (err) {
                          logger.error('Error downloading template:', err);
                          setMessage({ type: 'error', text: `Failed to download template: ${err.message}` });
                        }
                      }}
                    >
                      {safeT('downloadImportTemplate')}
                    </Button>
                  </Paper>
                </Box>
              </>
            )}
          </Box>
        )}

        {/* Lusha Credits Tab */}
        {activeTab === 1 && (
          <Box>
            {lushaLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography color="textSecondary" gutterBottom>
                          {safeT('totalCreditsUsed')}
                        </Typography>
                        <Typography variant="h4" component="div">
                          {(lushaData.summary.total_credits || 0) + (lushaData.personSummary.total_credits || 0)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                          Enrichment: {lushaData.summary.total_credits || 0} | Person Search: {lushaData.personSummary.total_credits || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={6}>
                    <Card>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography color="textSecondary" gutterBottom>
                          {safeT('totalEnrichments')}
                        </Typography>
                        <Typography variant="h4" component="div">
                          {(lushaData.summary.total_enrichments || 0) + (lushaData.personSummary.total_searches || 0)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                          Enrichments: {lushaData.summary.total_enrichments || 0} | Person Searches: {lushaData.personSummary.total_searches || 0}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Recent Credit Usage */}
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {safeT('recentCreditUsage')}
                </Typography>
                <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{safeT('date')}</TableCell>
                        <TableCell>{safeT('contactName')}</TableCell>
                        <TableCell>{safeT('company')}</TableCell>
                        <TableCell>{safeT('credits')}</TableCell>
                        <TableCell>Enrichment Source</TableCell>
                        <TableCell>{safeT('type')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(() => {
                        // Combine and sort both types of credit usage
                        const combinedUsage = [
                          // Contact enrichment credits
                          ...lushaData.creditUsage.map(usage => {
                            const enrichedContact = lushaData.enrichedContacts.find(
                              contact => contact.contact_id === usage.contact_id
                            );
                            return {
                              ...usage,
                              type: 'enrichment',
                              date: usage.enrichment_date,
                              name: enrichedContact ? enrichedContact.full_name : 'Unknown',
                              jobTitle: enrichedContact ? enrichedContact.job_title : null,
                              company: enrichedContact ? enrichedContact.company_name || enrichedContact.company_name_from_record : '-'
                            };
                          }),
                          // Person search credits
                          ...lushaData.personCreditUsage.map(usage => ({
                            ...usage,
                            type: 'person_search',
                            date: usage.search_date,
                            name: usage.person_data?.name || 'Person Search',
                            jobTitle: usage.person_data?.jobTitle || null,
                            company: usage.person_data?.company || '-'
                          }))
                        ];
                        
                        // Sort by date descending and take top 15
                        const sortedUsage = combinedUsage
                          .sort((a, b) => new Date(b.date) - new Date(a.date))
                          .slice(0, 15);
                        
                        return sortedUsage.length > 0 ? sortedUsage.map((usage) => (
                          <TableRow key={`${usage.type}-${usage.id}`}>
                            <TableCell>
                              <Typography variant="caption">
                                {formatDate(usage.date)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {usage.name}
                              </Typography>
                              {usage.jobTitle && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {usage.jobTitle}
                                </Typography>
                              )}
                              {usage.type === 'person_search' && (
                                <Typography variant="caption" color="primary" display="block">
                                  {usage.search_method === 'email' ? `Email: ${usage.search_email}` : 
                                   usage.search_method === 'name' ? `${usage.search_first_name} ${usage.search_last_name}` :
                                   'LinkedIn Search'}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {usage.company}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={usage.credits_used} 
                                color={usage.credits_used > 5 ? 'error' : 'primary'} 
                                size="small" 
                              />
                            </TableCell>
                            <TableCell>
                              {usage.enrichment_source ? (
                                <Chip 
                                  label={usage.enrichment_source === 'salesforce' ? 'Salesforce' : usage.enrichment_source === 'direct' ? 'Direct' : usage.enrichment_source}
                                  color={usage.enrichment_source === 'salesforce' ? 'secondary' : 'default'}
                                  size="small"
                                />
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  -
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {usage.type === 'enrichment' ? (
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  {usage.enriched_email && (
                                    <Tooltip title={safeT('emailEnriched')}>
                                      <EmailIcon color="primary" fontSize="small" />
                                    </Tooltip>
                                  )}
                                  {usage.enriched_phone && (
                                    <Tooltip title={safeT('phoneEnriched')}>
                                      <PhoneIcon color="primary" fontSize="small" />
                                    </Tooltip>
                                  )}
                                  {usage.enriched_linkedin && (
                                    <Tooltip title={safeT('linkedinEnriched')}>
                                      <LinkedInIcon color="primary" fontSize="small" />
                                    </Tooltip>
                                  )}
                                </Box>
                              ) : (
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <Tooltip title="Person Search">
                                    <SearchIcon color="secondary" fontSize="small" />
                                  </Tooltip>
                                  {usage.search_method === 'email' && (
                                    <Tooltip title="Email Search">
                                      <EmailIcon color="secondary" fontSize="small" />
                                    </Tooltip>
                                  )}
                                  {usage.search_method === 'name' && (
                                    <Tooltip title="Name + Company Search">
                                      <PersonIcon color="secondary" fontSize="small" />
                                    </Tooltip>
                                  )}
                                  {usage.search_method === 'linkedin' && (
                                    <Tooltip title="LinkedIn Search">
                                      <LinkedInIcon color="secondary" fontSize="small" />
                                    </Tooltip>
                                  )}
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        )) : (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ textAlign: 'center', py: 3 }}>
                              <Typography color="textSecondary">
                                {safeT('noCreditUsageFound')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })()}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Box>
        )}

        {/* Subscription Tab */}
        {activeTab === 2 && (
          <Box>
            {subscriptionLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                  <SubscriptionsIcon sx={{ mr: 1 }} />
                  Subscription Status
                </Typography>

                {/* Current Subscription Status */}
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" gutterBottom>
                          Current Status
                        </Typography>
                        {subscriptionData.status ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {subscriptionData.status.status === 'active' ? (
                              <>
                                <CheckCircleIcon color="success" />
                                <Chip label="Active Subscription" color="success" />
                              </>
                            ) : subscriptionData.status.status === 'trial' ? (
                              <>
                                <AccessTimeIcon color="warning" />
                                <Chip label="Trial Period" color="warning" />
                              </>
                            ) : subscriptionData.status.status === 'cancelled' && subscriptionData.status.is_cancelled_but_active ? (
                              <>
                                <WarningIcon color="warning" />
                                <Chip label="Cancelled - Access Until End Date" color="warning" />
                              </>
                            ) : (
                              <>
                                <CancelIcon color="error" />
                                <Chip label="No Active Subscription" color="error" />
                              </>
                            )}
                          </Box>
                        ) : (
                          <Chip label="No Subscription" color="default" />
                        )}
                      </Grid>
                      
                      {subscriptionData.status && subscriptionData.status.days_remaining > 0 && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle1" gutterBottom>
                            Days Remaining
                          </Typography>
                          <Chip 
                            label={`${subscriptionData.status.days_remaining} days`} 
                            color={subscriptionData.status.days_remaining < 7 ? 'error' : 'primary'}
                          />
                        </Grid>
                      )}
                    </Grid>

                    {/* Trial/Subscription Dates */}
                    {subscriptionData.subscription && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          Subscription Details
                        </Typography>
                        <List dense>
                          {subscriptionData.subscription.trial_start_date && (
                            <ListItem>
                              <ListItemIcon>
                                <AccessTimeIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText 
                                primary="Trial Period" 
                                secondary={`${formatDate(subscriptionData.subscription.trial_start_date)} - ${formatDate(subscriptionData.subscription.trial_end_date)}`}
                              />
                            </ListItem>
                          )}
                          {subscriptionData.subscription.subscription_start_date && (
                            <ListItem>
                              <ListItemIcon>
                                <CheckCircleIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText 
                                primary="Active Subscription" 
                                secondary={`${formatDate(subscriptionData.subscription.subscription_start_date)} - ${formatDate(subscriptionData.subscription.subscription_end_date)}`}
                              />
                            </ListItem>
                          )}
                        </List>
                      </Box>
                    )}

                    {/* Cancel Subscription Button for Active Subscriptions */}
                    {subscriptionData.status?.status === 'active' && (
                      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={handleCancelSubscription}
                          disabled={subscriptionActionLoading}
                          startIcon={subscriptionActionLoading ? <CircularProgress size={16} /> : <CancelIcon />}
                        >
                          Cancel Subscription
                        </Button>
                      </Box>
                    )}

                    {/* Show information for cancelled subscriptions */}
                    {subscriptionData.status?.status === 'cancelled' && subscriptionData.status.is_cancelled_but_active && (
                      <Box sx={{ mt: 2 }}>
                        <Alert severity="info">
                          Your subscription has been cancelled. You will continue to have access until {formatDate(subscriptionData.subscription?.subscription_end_date)}.
                        </Alert>
                      </Box>
                    )}

                    {/* Active Add-Ons Section */}
                    {activeAddons.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          Active Add-Ons
                        </Typography>
                        <Grid container spacing={1}>
                          {activeAddons.map((addon) => {
                            const isTrial = addon.status === 'trial' || addon.is_trial;
                            const daysRemaining = addon.days_remaining;
                            return (
                              <Grid item xs={12} sm={6} key={addon.id}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    p: 1.5,
                                    border: '1px solid',
                                    borderColor: isTrial ? 'warning.main' : 'success.main',
                                    borderRadius: 1,
                                    backgroundColor: isTrial ? 'rgba(255, 152, 0, 0.08)' : 'rgba(76, 175, 80, 0.08)'
                                  }}
                                >
                                  {isTrial ? (
                                    <AccessTimeIcon color="warning" fontSize="small" />
                                  ) : (
                                    <CheckCircleIcon color="success" fontSize="small" />
                                  )}
                                  <Box sx={{ flexGrow: 1 }}>
                                    <Typography variant="body2" fontWeight="medium">
                                      {addon.addon?.name || 'Add-On'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {isTrial 
                                        ? (daysRemaining !== undefined && daysRemaining !== null 
                                            ? `Trial - ${daysRemaining} ${daysRemaining === 1 ? 'day' : 'days'} remaining`
                                            : 'Trial period')
                                        : 'Active'}
                                    </Typography>
                                  </Box>
                                  <Chip
                                    label={isTrial ? 'Trial' : 'Active'}
                                    color={isTrial ? 'warning' : 'success'}
                                    size="small"
                                  />
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    )}
                  </CardContent>
                </Card>

                {/* Available Plans */}
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Available Plans
                </Typography>
                <Grid container spacing={2}>
                  {(subscriptionData.plans || []).map((plan) => (
                    <Grid item xs={12} md={6} key={plan.id}>
                      <Card 
                        sx={{ 
                          border: plan.is_featured ? '2px solid' : '1px solid',
                          borderColor: plan.is_featured ? 'primary.main' : 'divider',
                          position: 'relative'
                        }}
                      >
                        {plan.is_featured && (
                          <Box sx={{ 
                            position: 'absolute', 
                            top: -10, 
                            right: 10, 
                            backgroundColor: 'primary.main', 
                            color: 'white',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}>
                            <StarIcon fontSize="small" />
                            <Typography variant="caption">Popular</Typography>
                          </Box>
                        )}
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            {plan.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {plan.description}
                          </Typography>
                          <Typography variant="h4" sx={{ mb: 1 }}>
                            €{plan.price_eur}
                            <Typography component="span" variant="body2" color="text.secondary">
                              /{plan.billing_period}
                            </Typography>
                          </Typography>
                          
                          {/* Plan Features */}
                          <List dense sx={{ mb: 2 }}>
                            {plan.max_lusha_credits && (
                              <ListItem sx={{ px: 0 }}>
                                <ListItemIcon sx={{ minWidth: 30 }}>
                                  <CheckCircleIcon color="success" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText 
                                  primary={`${plan.max_lusha_credits} Lusha credits/month`}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                />
                              </ListItem>
                            )}
                            <ListItem sx={{ px: 0 }}>
                              <ListItemIcon sx={{ minWidth: 30 }}>
                                <CheckCircleIcon color="success" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText 
                                primary="Full platform access"
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                            <ListItem sx={{ px: 0 }}>
                              <ListItemIcon sx={{ minWidth: 30 }}>
                                <CheckCircleIcon color="success" fontSize="small" />
                              </ListItemIcon>
                              <ListItemText 
                                primary="AI-powered research tools"
                                primaryTypographyProps={{ variant: 'body2' }}
                              />
                            </ListItem>
                          </List>

                          <Button
                            variant={plan.is_featured ? 'contained' : 'outlined'}
                            fullWidth
                            disabled={subscriptionActionLoading}
                            onClick={() => handleSubscribe(plan.id)}
                          >
                            {subscriptionActionLoading ? (
                              <CircularProgress size={20} />
                            ) : (
                              subscriptionData.status?.status === 'active' ? 'Change Plan' : 'Subscribe'
                            )}
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                  
                  {/* Show message if no plans available */}
                  {(!subscriptionData.plans || subscriptionData.plans.length === 0) && !subscriptionLoading && (
                    <Grid item xs={12}>
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                          No subscription plans available at the moment.
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>

                {/* Add-Ons Section */}
                {(subscriptionData.status?.status === 'active' || subscriptionData.status?.status === 'trial' || (subscriptionData.status?.status === 'cancelled' && subscriptionData.status?.is_cancelled_but_active)) && (
                  <Box 
                    ref={addOnsSectionRef}
                    sx={{ mt: 4 }}
                    id="add-ons-section"
                  >
                    <AddOnsSection />
                  </Box>
                )}
              </>
            )}
          </Box>
        )}

        {/* Team Members Tab (Supervisors only) */}
        {user?.role_type === 'Supervisor' && activeTab === 3 && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <GroupIcon sx={{ mr: 1 }} />
              Team Members
            </Typography>
            {teamLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : teamMembers.length > 0 ? (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Username</TableCell>
                      <TableCell>Display Name</TableCell>
                      <TableCell>Email</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {teamMembers.map((member) => (
                      <TableRow key={member.id}>
                        <TableCell>{member.username}</TableCell>
                        <TableCell>{member.display_name}</TableCell>
                        <TableCell>{member.email}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="textSecondary" sx={{ mt: 2 }}>
                No team members assigned.
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saveLoading}>
          {safeT('cancel')}
        </Button>
        {activeTab === 0 && (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saveLoading}
            startIcon={saveLoading ? <CircularProgress size={16} /> : null}
          >
            {saveLoading ? safeT('loading') : safeT('saveChanges')}
          </Button>
        )}
      </DialogActions>

      {/* Subscription Confirmation Modal */}
      <Dialog
        open={confirmationModal.open}
        onClose={() => setConfirmationModal({ ...confirmationModal, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          <Typography variant="h6">
            {confirmationModal.action === 'cancel' 
              ? 'Cancel Subscription' 
              : confirmationModal.isChangePlan 
                ? 'Change Plan' 
                : 'Subscribe to Plan'
            }
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {confirmationModal.action === 'cancel' ? (
              <>
                Are you sure you want to cancel your subscription to <strong>{confirmationModal.planName}</strong>?
                <br /><br />
                Your subscription will remain active until the end of your current billing period, after which you will lose access to premium features.
              </>
            ) : confirmationModal.isChangePlan ? (
              <>
                Are you sure you want to change your plan to <strong>{confirmationModal.planName}</strong> for <strong>€{confirmationModal.planPrice}</strong>?
                <br /><br />
                Your billing will be updated immediately, and you'll have access to the new plan features right away.
              </>
            ) : (
              <>
                Are you sure you want to subscribe to <strong>{confirmationModal.planName}</strong> for <strong>€{confirmationModal.planPrice}</strong>?
                <br /><br />
                You will be charged immediately and gain access to all premium features.
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmationModal({ ...confirmationModal, open: false })}
            disabled={subscriptionActionLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmationModal.action === 'cancel' ? handleConfirmCancellation : handleConfirmSubscription}
            variant="contained"
            color={confirmationModal.action === 'cancel' ? 'error' : 'primary'}
            disabled={subscriptionActionLoading}
            startIcon={subscriptionActionLoading ? <CircularProgress size={16} /> : null}
          >
            {subscriptionActionLoading ? 'Processing...' : (
              confirmationModal.action === 'cancel' 
                ? 'Cancel Subscription' 
                : confirmationModal.isChangePlan 
                  ? 'Change Plan' 
                  : 'Subscribe'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default UserSettingsDialog; 